"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.compareDatabaseProjectionTypedValues = exports.Compare = void 0;
exports.databaseProjectionColumnSortType = databaseProjectionColumnSortType;
exports.databaseRowViewOrder = databaseRowViewOrder;
exports.sortDatabaseRowsAsView = sortDatabaseRowsAsView;
const database_projection_groups_1 = require("./database-projection-groups");
exports.Compare = {
    GT: 'GT',
    LT: 'LT',
};
const compareList = (listA, listB, compare) => {
    let i = 0;
    while (i < listA.length && i < listB.length) {
        const a = listA[i];
        const b = listB[i];
        const result = compare(a, b);
        if (result !== 0) {
            return result;
        }
        i++;
    }
    return 0;
};
const compareString = (a, b) => {
    const aEmpty = typeof a !== 'string' || a === '';
    const bEmpty = typeof b !== 'string' || b === '';
    if (aEmpty && bEmpty) {
        return 0;
    }
    if (aEmpty) {
        return exports.Compare.GT;
    }
    if (bEmpty) {
        return exports.Compare.LT;
    }
    const listA = a.split('.');
    const listB = b.split('.');
    return compareList(listA, listB, (a, b) => {
        const lowA = a.toLowerCase();
        const lowB = b.toLowerCase();
        const numberA = Number.parseInt(lowA);
        const numberB = Number.parseInt(lowB);
        const aIsNaN = Number.isNaN(numberA);
        const bIsNaN = Number.isNaN(numberB);
        if (aIsNaN && !bIsNaN) {
            return 1;
        }
        if (!aIsNaN && bIsNaN) {
            return -1;
        }
        if (!aIsNaN && !bIsNaN && numberA !== numberB) {
            return numberA - numberB;
        }
        return lowA.localeCompare(lowB);
    });
};
const compareNumber = (a, b) => {
    if (a == null && b == null) {
        return 0;
    }
    if (a == null) {
        return exports.Compare.GT;
    }
    if (b == null) {
        return exports.Compare.LT;
    }
    return Number(a) - Number(b);
};
const compareBoolean = (a, b) => {
    if (a == null && b == null) {
        return 0;
    }
    if (a == null) {
        return exports.Compare.GT;
    }
    if (b == null) {
        return exports.Compare.LT;
    }
    a = Boolean(a);
    b = Boolean(b);
    const bA = a ? 1 : 0;
    const bB = b ? 1 : 0;
    return bA - bB;
};
const compareArray = (type, a, b) => {
    if (!Array.isArray(a) && !Array.isArray(b)) {
        return 0;
    }
    if (!Array.isArray(a)) {
        return exports.Compare.GT;
    }
    if (!Array.isArray(b)) {
        return exports.Compare.LT;
    }
    return compareList(a, b, (a, b) => {
        return compare(type.element, a, b);
    });
};
const compareAny = (a, b) => {
    if (a == null && b == null) {
        return 0;
    }
    if (a == null) {
        return exports.Compare.GT;
    }
    if (b == null) {
        return exports.Compare.LT;
    }
    const numeric = Number(a) - Number(b);
    return Number.isNaN(numeric)
        ? a.toString().localeCompare(b.toString())
        : numeric;
};
const compareTag = (type, a, b) => {
    if (a == null && b == null) {
        return 0;
    }
    if (a == null) {
        return exports.Compare.GT;
    }
    if (b == null) {
        return exports.Compare.LT;
    }
    const indexA = type.data?.findIndex(tag => tag.id === a) ?? -1;
    const indexB = type.data?.findIndex(tag => tag.id === b) ?? -1;
    if (indexA >= 0 && indexB >= 0) {
        return indexA - indexB;
    }
    if (indexA >= 0) {
        return exports.Compare.LT;
    }
    if (indexB >= 0) {
        return exports.Compare.GT;
    }
    return compareString(a, b);
};
const compare = (type, a, b) => {
    if (type.name === 'RichText') {
        return compareString(a?.toString(), b?.toString());
    }
    if (type.name === 'String') {
        return compareString(a, b);
    }
    if (type.name === 'Number') {
        return compareNumber(a, b);
    }
    if (type.name === 'Date') {
        return compareNumber(a, b);
    }
    if (type.name === 'Boolean') {
        return compareBoolean(a, b);
    }
    if (type.name === 'Tag') {
        return compareTag(type, a, b);
    }
    if (type.name === 'array' && type.element) {
        return compareArray(type, a, b);
    }
    return compareAny(a, b);
};
/**
 * Compare two values with the canonical data-view type semantics.
 * Empty values remain last for both directions; only concrete value order is
 * reversed for descending comparisons.
 */
const compareDatabaseProjectionTypedValues = (type, a, b, desc = false) => {
    const result = compare(type, a, b);
    if (typeof result === 'number') {
        return desc ? -result : result;
    }
    return result === exports.Compare.GT ? 1 : -1;
};
exports.compareDatabaseProjectionTypedValues = compareDatabaseProjectionTypedValues;
/** How the table view compares the values of `column`: by its type, a select by the order of its options. */
function databaseProjectionColumnSortType(column) {
    const name = column?.logicalType?.name ?? 'Unknown';
    const options = column ? (0, database_projection_groups_1.databaseProjectionOptions)(column) : [];
    const element = column?.logicalType?.element;
    return {
        name,
        data: options,
        ...(element
            ? {
                element: element.name === 'Tag'
                    ? { name: 'Tag', data: options }
                    : { name: element.name },
            }
            : {}),
    };
}
function databaseRowViewOrder(sorts, columns) {
    const byId = new Map(columns.map(column => [column.id, column]));
    const keyed = sorts.map(sort => {
        const column = byId.get(sort.propertyId);
        return {
            propertyId: sort.propertyId,
            desc: sort.direction === 'desc',
            column,
            type: databaseProjectionColumnSortType(column),
        };
    });
    return {
        keys: read => Object.fromEntries(keyed.map(({ propertyId, column }) => {
            const raw = read(propertyId);
            return [
                propertyId,
                column ? (0, database_projection_groups_1.normalizeDatabaseProjectionCell)(raw, column) : raw,
            ];
        })),
        compare: (left, right) => {
            for (const { propertyId, desc, type } of keyed) {
                const order = (0, exports.compareDatabaseProjectionTypedValues)(type, left[propertyId], right[propertyId], desc);
                if (order)
                    return order;
            }
            return 0;
        },
    };
}
/**
 * `rows` in the order the table view gives them for `sorts`
 * (databaseRowViewOrder); ties keep the order of `rows`. Each row's cells are
 * read and normalized once, not once per comparison.
 */
function sortDatabaseRowsAsView(rows, sorts, columns, readValue) {
    if (!sorts.length)
        return rows.slice();
    const order = databaseRowViewOrder(sorts, columns);
    return rows
        .map(row => ({ row, keys: order.keys(id => readValue(row, id)) }))
        .sort((left, right) => order.compare(left.keys, right.keys))
        .map(({ row }) => row);
}
